/**
 * Core-100 R4 — CORE-AZ / CORE-AF presentation rules for the customer's own replies and for how a
 * message came to be on a ticket.
 *
 * CORE-AZ: "A customer's reply SHALL be recorded as theirs ... an absence of reply MUST NOT be read as
 * either confirmation or cancellation. Unknown remains unknown." and "CORE-AZ MUST NOT make or alter
 * commitments": a CANCEL text is the customer's REQUEST — the appointment is unchanged until the shop
 * decides — so it is shown to the advisor as a request, never as a cancellation.
 *
 * CORE-AF: "Association MUST NOT be asserted with false confidence. Where the link is inferred, that
 * inference and its confidence SHALL be recorded as such" — and shown as such.
 */

export interface CustomerReplyLabel {
  kind: 'confirmed' | 'cancel_requested';
  text: string;
  detail: string;
}

/** tickets.customer_confirmed → what the advisor sees. NULL (no reply) renders NOTHING. */
export function customerReplyLabel(customerConfirmed: boolean | null | undefined): CustomerReplyLabel | null {
  if (customerConfirmed === true) {
    return { kind: 'confirmed', text: 'Customer confirmed', detail: 'The customer replied CONFIRM by text.' };
  }
  if (customerConfirmed === false) {
    return {
      kind: 'cancel_requested',
      text: 'Customer asked to cancel',
      detail: 'The customer replied CANCEL by text. The appointment is unchanged until the shop decides.',
    };
  }
  return null;
}

const METHOD_TEXT: Record<string, (a: Record<string, unknown>) => string> = {
  single_open_ticket: () => "the customer's only open ticket",
  earliest_of_n: (a) => `the earliest of ${Number(a.candidates) || 'several'} open tickets (shop rule)`,
  newest_of_n: (a) => `the newest of ${Number(a.candidates) || 'several'} open tickets (shop rule)`,
  confirm_earliest_pending: (a) =>
    Number(a.candidates) > 1
      ? `the earliest of ${Number(a.candidates)} appointments awaiting confirmation`
      : 'the appointment awaiting confirmation',
  single_open_ticket_for_caller: () => "the caller's only open ticket",
};

/**
 * How an event came to be on this ticket, for display. Returns null when nothing should be said (an
 * event a person wrote on the ticket, or one with no association record).
 */
export function associationLabel(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const a = (payload as Record<string, unknown>).association;
  if (!a || typeof a !== 'object') return null;
  const assoc = a as Record<string, unknown>;
  if (assoc.method === 'person') return 'Linked by a team member';
  if (assoc.inferred === true) {
    const describe = typeof assoc.method === 'string' ? METHOD_TEXT[assoc.method] : undefined;
    return describe ? `Linked automatically — ${describe(assoc)}` : 'Linked automatically';
  }
  return null;
}

/** An inbound text whose action is a cancellation REQUEST, said as such. */
export function cancelRequestNote(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  return (payload as Record<string, unknown>).action === 'cancel_request'
    ? 'Customer asked to cancel — the appointment is unchanged until the shop decides.'
    : null;
}
